import React, { useState, useRef, useEffect } from 'react';
import { MapPin, ChevronDown, Check } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const CitySelector = ({ selectedCities, onChange, cityCounts = {}, loading, availableCities }) => {
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const toggleCity = (city) => {
        if (selectedCities.includes(city)) {
            onChange(selectedCities.filter(c => c !== city));
        } else {
            onChange([...selectedCities, city]);
        }
    };

    const toggleSector = (sector) => {
        const citiesInSector = sector.cities;
        const toggleableCities = citiesInSector.filter(city => {
            if (availableCities && availableCities.length > 0) return true;
            return loading || (cityCounts[city] || 0) > 0;
        });

        if (toggleableCities.length === 0) return;

        const allSelected = toggleableCities.every(city => selectedCities.includes(city));

        if (allSelected) {
            onChange(selectedCities.filter(c => !toggleableCities.includes(c)));
        } else {
            const otherCities = selectedCities.filter(c => !toggleableCities.includes(c));
            onChange([...otherCities, ...toggleableCities]);
        }
    };

    const displayValue = selectedCities.length === 0
        ? "Toutes les villes"
        : selectedCities.length === 1
            ? selectedCities[0]
            : `${selectedCities.length} villes sélectionnées`;

    let citiesToRender = [];

    if (availableCities && availableCities.length > 0) {
        citiesToRender = availableCities
            .map(c => typeof c === 'object' ? c.name : c)
            .filter(cityName => (cityCounts[cityName] || 0) > 0)
            .sort((a, b) => a.localeCompare(b));
    }



    return (
        <div className="relative group" ref={containerRef}>
            <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-[#EA723D] transition-colors z-10" size={20} />

            <button
                onClick={() => setIsOpen(!isOpen)}
                className={`w-full text-left pl-12 pr-4 h-[58px] bg-gray-50 border border-gray-200 rounded-xl text-[#2C353D] font-bold focus:ring-2 focus:ring-[#EA723D]/20 focus:border-[#EA723D] outline-none transition-all cursor-pointer hover:bg-gray-100 flex items-center justify-between ${isOpen ? 'ring-2 ring-[#EA723D]/20 border-[#EA723D]' : ''}`}
            >
                <span className="truncate">{displayValue}</span>
                <ChevronDown size={16} className={`text-gray-400 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} />
            </button>

            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 10 }}
                        transition={{ duration: 0.2 }}
                        className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-2xl border border-gray-100 overflow-hidden z-[100] max-h-[400px] overflow-y-auto"
                    >
                        <div className="p-4 space-y-2">
                            <div
                                onClick={() => onChange([])}
                                className={`flex items-center gap-3 p-3 rounded-lg transition-colors cursor-pointer hover:bg-gray-50 border-b border-gray-100 mb-2 ${selectedCities.length === 0
                                    ? 'bg-[#2C353D]/5 text-[#2C353D]'
                                    : 'text-gray-600'
                                    }`}
                            >
                                <div className={`w-5 h-5 rounded border flex items-center justify-center transition-colors ${selectedCities.length === 0 ? 'bg-[#2C353D] border-[#2C353D]' : 'border-gray-300 bg-white'}`}>
                                    {selectedCities.length === 0 && <Check size={12} className="text-white" />}
                                </div>
                                <span className="text-sm font-black uppercase tracking-wider">
                                    Toutes les villes
                                </span>
                            </div>

                            <div className="grid grid-cols-1 gap-1">
                                {citiesToRender.map((city) => {
                                    const isSelected = selectedCities.includes(city);
                                    const count = cityCounts[city] || 0;

                                    return (
                                        <div
                                            key={city}
                                            onClick={() => toggleCity(city)}
                                            className={`flex items-center gap-3 p-3 rounded-xl transition-all cursor-pointer hover:bg-gray-50 ${isSelected ? 'bg-orange-50 text-[#EA723D] border-orange-100' : 'text-gray-600 border-transparent'} border`}
                                        >

                                            <div className={`w-5 h-5 rounded-lg border flex items-center justify-center transition-all ${isSelected ? 'bg-[#EA723D] border-[#EA723D] scale-110' : 'border-gray-200 bg-white'}`}>
                                                {isSelected && <Check size={12} className="text-white" strokeWidth={4} />}
                                            </div>
                                            <span className={`text-sm flex-1 flex justify-between items-center ${isSelected ? 'font-bold' : 'font-medium'}`}>
                                                {city}
                                                {!loading && count > 0 && (
                                                    <span className={`text-[10px] px-2 py-0.5 rounded-full ${isSelected ? 'bg-white/50 text-[#EA723D]' : 'bg-gray-100 text-gray-400'}`}>
                                                        {count}
                                                    </span>
                                                )}
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default CitySelector;
